import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { FiRefreshCw, FiPlay, FiSquare, FiCheck, FiServer, FiDatabase, FiAlertTriangle, FiTrash2 } from 'react-icons/fi';
import { useConfirm } from '../ConfirmDialog';
import './QpmCollectionPanel.css';

/**
 * 수집 설정 › 「QPM 지표」(2026-11-04) — 서버·DB 성능 지표를 QPM 자원 조회 API 에서 가져와 쌓는다.
 * 대상·지표 목록은 QPM /catalog 가 준다(코드에 이름을 적지 않는다). 주기·보존·시작/중지는 여기서 정한다.
 * 관리자만(서버가 막는다). QPM 이 없는 설치에서는 「QPM 연결 안 됨」 이라고 말하고 다른 탭은 그대로다.
 * 서버: backend/modules/monitoring/qpm/
 */
const fmtTime = (s) => (s ? String(s).replace('T', ' ').slice(0, 19) : '-');

export default function QpmCollectionPanel({ apiBase, onMessage }) {
  const { t } = useTranslation();
  const confirm = useConfirm();
  const base = `${apiBase}/sql-developer/monitoring/qpm`;
  const [data, setData] = useState(null);           // { qpm, collections, minIntervalSec }
  const [denied, setDenied] = useState(false);
  const [loading, setLoading] = useState(false);
  const [selected, setSelected] = useState(null);   // 대상 키
  const [draft, setDraft] = useState(null);         // { metrics:Set, intervalSec, retainDays, maxMb }

  const errText = useCallback((j, fallback) => (
    j && j.errorType ? t(`monitoring:qpm.err.${j.errorType}`, { defaultValue: j.error || fallback }) : ((j && j.error) || fallback)
  ), [t]);

  const load = useCallback(async (fresh = false) => {
    setLoading(true);
    try {
      const res = await fetch(`${base}${fresh ? '?fresh=1' : ''}`);
      if (res.status === 403) { setDenied(true); return; }
      const j = await res.json();
      if (!res.ok) throw new Error(errText(j, 'QPM'));
      setDenied(false);
      setData(j);
    } catch (err) {
      onMessage && onMessage('error', err.message);
    } finally {
      setLoading(false);
    }
  }, [base, errText, onMessage]);

  useEffect(() => { load(); }, [load]);

  const qpm = data && data.qpm;
  const collections = useMemo(() => (data && data.collections) || [], [data]);
  const byTarget = useMemo(() => new Map(collections.map(c => [c.target, c])), [collections]);
  // 목록 = QPM 이 아는 대상 + 정의는 있는데 QPM 에서 빠진 대상(사라졌다고 말한다)
  const rows = useMemo(() => {
    const cat = (qpm && qpm.reachable && qpm.targets) || [];
    const list = cat.map(x => ({ target: x.target, kind: x.kind, name: x.name, up: x.up, metrics: x.metrics || [], engine: x.engine, inCatalog: true }));
    collections.forEach((c) => {
      if (!list.some(x => x.target === c.target)) list.push({ target: c.target, kind: c.kind, name: c.name || c.target, up: null, metrics: c.metrics || [], inCatalog: !(qpm && qpm.reachable) ? null : false });
    });
    return list;
  }, [qpm, collections]);
  const cur = rows.find(r => r.target === selected) || null;
  const curCfg = cur ? byTarget.get(cur.target) : null;
  const defs = useMemo(() => {
    if (!cur || !qpm || !qpm.metrics) return [];
    const all = cur.kind === 'host' ? qpm.metrics.host : qpm.metrics.database;
    return (all || []).filter(d => cur.metrics.includes(d.key));
  }, [cur, qpm]);

  useEffect(() => {
    if (!selected && rows.length) setSelected(rows[0].target);
  }, [rows, selected]);
  useEffect(() => {
    if (!cur) { setDraft(null); return; }
    setDraft({
      metrics: new Set(curCfg ? curCfg.metrics || [] : []),
      intervalSec: curCfg ? curCfg.intervalSec : 30,
      retainDays: curCfg ? curCfg.retainDays : 7,
      maxMb: curCfg ? curCfg.maxMb : 500,
    });
    // 대상을 바꿀 때만 초안을 새로 — 저장·새로고침으로 바뀐 정의도 반영한다
  }, [cur && cur.target, curCfg]); // eslint-disable-line react-hooks/exhaustive-deps

  const call = async (method, path, body, okMsg) => {
    setLoading(true);
    try {
      const res = await fetch(`${base}/collections/${encodeURIComponent(selected)}${path}`, {
        method,
        headers: body ? { 'Content-Type': 'application/json' } : undefined,
        body: body ? JSON.stringify(body) : undefined,
      });
      const j = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(errText(j, `HTTP ${res.status}`));
      if (okMsg) onMessage && onMessage('success', okMsg);
      await load();
      return true;
    } catch (err) {
      onMessage && onMessage('error', err.message);
      return false;
    } finally {
      setLoading(false);
    }
  };

  const save = () => call('PUT', '', {
    metrics: [...draft.metrics], intervalSec: Number(draft.intervalSec), retainDays: Number(draft.retainDays), maxMb: Number(draft.maxMb),
  }, t('monitoring:qpm.saved', { name: cur.name }));
  const start = () => call('POST', '/start', null, t('monitoring:qpm.started', { name: cur.name }));
  const stop = () => call('POST', '/stop', null, t('monitoring:qpm.stopped', { name: cur.name }));
  const remove = async () => {
    const ok = await confirm({
      title: t('monitoring:qpm.removeTitle'),
      message: t('monitoring:qpm.removeMsg', { name: cur.name }),
      confirmText: t('monitoring:qpm.remove'),
      variant: 'danger',
    });
    if (ok) { await call('DELETE', '', null, t('monitoring:qpm.removed', { name: cur.name })); setSelected(null); }
  };

  if (denied) return <div className="qpm-empty">{t('monitoring:qpm.adminOnly')}</div>;
  if (!data) return <div className="qpm-empty">{t('monitoring:qpm.loading')}</div>;

  const toggle = (key) => setDraft((d) => {
    const next = new Set(d.metrics);
    if (next.has(key)) next.delete(key); else next.add(key);
    return { ...d, metrics: next };
  });
  const dirty = draft && curCfg
    ? (draft.metrics.size !== (curCfg.metrics || []).length || (curCfg.metrics || []).some(k => !draft.metrics.has(k))
      || Number(draft.intervalSec) !== curCfg.intervalSec || Number(draft.retainDays) !== curCfg.retainDays || Number(draft.maxMb) !== curCfg.maxMb)
    : !!(draft && draft.metrics.size);
  const run = curCfg && curCfg.lastRun;

  return (
    <div className="qpm-panel">
      <div className="qpm-head">
        <div className="qpm-head-text">
          <strong>{t('monitoring:qpm.title')}</strong>
          <span className="qpm-sub">{t('monitoring:qpm.intro')}</span>
        </div>
        <span className={`qpm-conn ${qpm.reachable ? 'ok' : 'bad'}`} title={qpm.url}>
          {qpm.reachable ? t('monitoring:qpm.connected', { url: qpm.url }) : t('monitoring:qpm.unreachable', { url: qpm.url })}
        </span>
        <button type="button" className="app-btn" onClick={() => load(true)} disabled={loading} title={t('monitoring:qpm.refresh')}>
          <FiRefreshCw size={13} /> {t('monitoring:qpm.refresh')}
        </button>
      </div>
      {!qpm.reachable && (
        <div className="qpm-warn"><FiAlertTriangle size={13} /> {errText(qpm, '')}</div>
      )}

      <div className="qpm-body">
        <ul className="qpm-targets" role="listbox" aria-label={t('monitoring:qpm.targets')}>
          {rows.length === 0 && <li className="qpm-empty-row">{t('monitoring:qpm.noTargets')}</li>}
          {rows.map((r) => {
            const c = byTarget.get(r.target);
            return (
              <li key={r.target}>
                <button type="button" role="option" aria-selected={r.target === selected}
                  className={`qpm-target ${r.target === selected ? 'active' : ''}`} onClick={() => setSelected(r.target)}>
                  {r.kind === 'host' ? <FiServer size={13} /> : <FiDatabase size={13} />}
                  <span className="qpm-target-name">{r.name}</span>
                  <span className={`qpm-badge ${c && c.running ? 'on' : ''} ${r.inCatalog === false ? 'gone' : ''}`}>
                    {r.inCatalog === false ? t('monitoring:qpm.gone')
                      : c && c.running ? t('monitoring:qpm.collecting')
                        : c ? t('monitoring:qpm.stoppedBadge') : r.up === false ? t('monitoring:qpm.down') : ''}
                  </span>
                </button>
              </li>
            );
          })}
        </ul>

        {cur && draft && (
          <div className="qpm-detail" key={cur.target}>
            <div className="qpm-detail-title">
              <span>{cur.name}</span>
              <code className="qpm-key">{cur.target}</code>
              {cur.up === false && <span className="qpm-badge">{t('monitoring:qpm.down')}</span>}
            </div>
            {cur.inCatalog === false && <div className="qpm-warn"><FiAlertTriangle size={13} /> {t('monitoring:qpm.goneHint')}</div>}

            <div className="qpm-section-label">{t('monitoring:qpm.metrics')}</div>
            {defs.length === 0 && <div className="qpm-empty-row">{qpm.reachable ? t('monitoring:qpm.noMetrics') : t('monitoring:qpm.needQpm')}</div>}
            <div className="qpm-metrics">
              {defs.map(d => (
                <label key={d.key} className="qpm-metric">
                  <input type="checkbox" checked={draft.metrics.has(d.key)} onChange={() => toggle(d.key)} disabled={loading} />
                  <span className="qpm-metric-label">{d.label || d.key}</span>
                  <span className="qpm-metric-meta">
                    {d.unit}{d.labelKey ? ` · ${t('monitoring:qpm.perLabel', { label: d.labelKey })}` : ''} · {t(`monitoring:qpm.kind.${d.kind}`, { defaultValue: d.kind })}
                  </span>
                </label>
              ))}
            </div>

            <div className="qpm-section-label">{t('monitoring:qpm.schedule')}</div>
            <div className="qpm-schedule">
              <label>{t('monitoring:qpm.interval')}
                <input type="number" min={data.minIntervalSec || 5} max={3600} value={draft.intervalSec}
                  onChange={e => setDraft(d => ({ ...d, intervalSec: e.target.value }))} disabled={loading} />
              </label>
              <label>{t('monitoring:qpm.retain')}
                <input type="number" min={1} max={365} value={draft.retainDays}
                  onChange={e => setDraft(d => ({ ...d, retainDays: e.target.value }))} disabled={loading} />
              </label>
              <label>{t('monitoring:qpm.maxMb')}
                <input type="number" min={10} max={10240} value={draft.maxMb}
                  onChange={e => setDraft(d => ({ ...d, maxMb: e.target.value }))} disabled={loading} />
              </label>
            </div>
            <div className="qpm-hint">{t('monitoring:qpm.intervalHint', { n: data.minIntervalSec || 5 })}</div>

            <div className="qpm-actions">
              <button type="button" className="app-btn app-btn-primary" onClick={save}
                disabled={loading || !dirty || draft.metrics.size === 0 || !qpm.reachable}>
                <FiCheck size={13} /> {t('monitoring:qpm.save')}
              </button>
              {curCfg && (curCfg.running
                ? <button type="button" className="app-btn" onClick={stop} disabled={loading}><FiSquare size={13} /> {t('monitoring:qpm.stop')}</button>
                : <button type="button" className="app-btn" onClick={start} disabled={loading || dirty}><FiPlay size={13} /> {t('monitoring:qpm.start')}</button>)}
              {curCfg && (
                <button type="button" className="app-btn app-btn-danger" onClick={remove} disabled={loading}><FiTrash2 size={13} /> {t('monitoring:qpm.remove')}</button>
              )}
              {dirty && curCfg && <span className="qpm-hint">{t('monitoring:qpm.unsaved')}</span>}
            </div>

            {curCfg && (
              <div className="qpm-status">
                <div><span>{t('monitoring:qpm.storeKey')}</span><code>{curCfg.connId}</code> · <code>metric_group = qpm</code></div>
                <div><span>{t('monitoring:qpm.stored')}</span>{t('monitoring:qpm.storedRows', { n: curCfg.storedRows || 0 })} · {fmtTime(curCfg.lastStoredAt)}</div>
                {run && (
                  <div className={run.ok ? '' : 'qpm-status-bad'}>
                    <span>{t('monitoring:qpm.lastRun')}</span>{fmtTime(run.lastAt)} · {run.ok
                      ? t('monitoring:qpm.runOk', { n: run.rows || 0 })
                      : errText(run, '')}
                    {run.missing && <> · {t('monitoring:qpm.missing', { keys: run.missing.join(', ') })}</>}
                  </div>
                )}
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
